import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Lock, X } from 'lucide-react';

import { useAuth } from '@/shared/auth/AuthContext';
import { Alert, AlertDescription } from '@/shared/components/ui/alert';
import { Button } from '@/shared/components/ui/button';

const DISMISS_KEY = 'trial_banner_dismissed_for_date';

/**
 * Self-serve trial countdown / locked banner (PLAN.md 2026-08-01, Faza A).
 * Mirrors BillingBanner's pattern but reads straight off `user.tenant`
 * (already carries isDemo/demoEndsAt/trialLockedAt via GET /me) — no
 * separate query needed.
 */
export function TrialBanner(): JSX.Element | null {
  const { t } = useTranslation();
  const { user } = useAuth();
  const tenant = user?.tenant;
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return tenant?.demoEndsAt ? sessionStorage.getItem(DISMISS_KEY) === tenant.demoEndsAt : false;
    } catch {
      return false;
    }
  });

  if (user?.role !== 'operator' || !tenant?.isDemo || !tenant.demoEndsAt) return null;

  if (tenant.trialLockedAt) {
    return (
      <Alert variant="destructive" className="rounded-none border-x-0 border-t-0">
        <Lock className="h-4 w-4" />
        <AlertDescription className="flex items-center justify-between gap-4">
          <span>
            {t('signup.trialBanner.locked')} — {t('signup.trialBanner.lockedDetail')}
          </span>
        </AlertDescription>
      </Alert>
    );
  }

  if (dismissed) return null;

  const daysLeft = Math.max(
    0,
    Math.ceil((new Date(tenant.demoEndsAt).getTime() - Date.now()) / (24 * 60 * 60 * 1000)),
  );
  if (daysLeft > 14) return null;

  return (
    <Alert className="rounded-none border-x-0 border-t-0">
      <AlertTriangle className="h-4 w-4" />
      <AlertDescription className="flex items-center justify-between gap-4">
        <span>{t('signup.trialBanner.daysLeft', { days: daysLeft })}</span>
        <Button
          variant="ghost"
          size="icon"
          className="shrink-0"
          onClick={() => {
            try {
              sessionStorage.setItem(DISMISS_KEY, tenant.demoEndsAt!);
            } catch {
              /* sessionStorage unavailable — dismiss is best-effort only */
            }
            setDismissed(true);
          }}
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      </AlertDescription>
    </Alert>
  );
}
